import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  Injector,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { merge } from 'rxjs';
import { MatRadioModule } from '@angular/material/radio';
import { CheckboxComponent } from '@vendasta/galaxy/checkbox';
import { TranslateModule } from '@ngx-translate/core';

export interface ToggleableSelectOption {
  value: string;
  label: string;
  testId?: string;
  /** When true, this option only appears as a radio button — never a checkbox. */
  exclusiveFromMultiSelect?: boolean;
}

export interface ViewSelection {
  values: string[];
  multiViewMode: boolean;
}

export function sameViewSelection(a: ViewSelection, b: ViewSelection): boolean {
  if (a.multiViewMode !== b.multiViewMode) return false;
  if (a.values.length !== b.values.length) return false;
  const bValues = new Set(b.values);
  return a.values.every((v) => bValues.has(v));
}

/**
 * Renders a list of options as radio buttons in single-select mode, toggling to
 * checkboxes when the user holds Shift or Shift-clicks an eligible option.
 * Exclusive options (e.g. "Closed") are never shown as checkboxes.
 */
@Component({
  selector: 'inbox-toggleable-view-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatRadioModule, CheckboxComponent, TranslateModule],
  templateUrl: './inbox-toggleable-view-select.component.html',
  styleUrl: './inbox-toggleable-view-select.component.scss',
})
export class InboxToggleableViewSelectComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly options = input.required<ToggleableSelectOption[]>();
  readonly selection = input.required<ViewSelection>();

  readonly selectionChange = output<ViewSelection>();

  protected readonly multiSelectOptions = computed(() =>
    this.options().filter((o) => !o.exclusiveFromMultiSelect),
  );

  protected readonly radioControl = new FormControl<string>('', { nonNullable: true });
  private readonly checkboxControls = new Map<string, FormControl<boolean>>();

  private readonly shiftHeld = signal(false);
  private readonly multiViewMode = signal(false);
  private applyingSelection = false;
  protected readonly showCheckboxes = computed(() => this.multiViewMode() || this.shiftHeld());

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Shift') return;
    this.shiftHeld.set(true);
    if (!this.multiViewMode()) {
      const current = this.radioControl.value;
      for (const [value, ctrl] of this.checkboxControls) {
        ctrl.setValue(value === current, { emitEvent: false });
      }
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    if (event.key !== 'Shift') return;
    this.shiftHeld.set(false);
    this.recalcMultiMode();
  }

  ngOnInit(): void {
    for (const opt of this.multiSelectOptions()) {
      this.checkboxControls.set(opt.value, new FormControl(false, { nonNullable: true }));
    }

    // emitEvent:false prevents valueChanges subscriptions below from re-emitting back to the parent.
    toObservable(this.selection, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ values, multiViewMode }) => this.applySelection(values, multiViewMode));

    this.subscribeCheckboxes();
    this.radioControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.emitSelection({ values: [v], multiViewMode: false }));
  }

  protected getCheckboxControl(value: string): FormControl<boolean> {
    const ctrl = this.checkboxControls.get(value);
    if (!ctrl) throw new Error(`No checkbox control for value: ${value}`);
    return ctrl;
  }

  protected onRadioClick(event: MouseEvent, value: string): void {
    const opt = this.options().find((o) => o.value === value);
    if (!event.shiftKey || opt?.exclusiveFromMultiSelect) return;
    event.preventDefault();
    event.stopPropagation();
    this.enterMultiMode(value);
  }

  private applySelection(values: string[], multiViewMode: boolean): void {
    this.applyingSelection = true;
    try {
      this.multiViewMode.set(multiViewMode);
      if (multiViewMode) {
        for (const [v, ctrl] of this.checkboxControls) {
          ctrl.setValue(values.includes(v), { emitEvent: false });
        }
      } else {
        this.radioControl.setValue(
          values[0] ?? (this.options()[0]?.value ?? ''),
          { emitEvent: false },
        );
      }
    } finally {
      this.applyingSelection = false;
    }
  }

  // Only surface genuine user-driven changes. Suppress emissions while applying
  // a parent-provided selection, and never echo a selection equal to the current
  // input — both would feed back into [selection] and spin change detection (NG0103).
  private emitSelection(next: ViewSelection): void {
    if (this.applyingSelection) return;
    if (sameViewSelection(next, this.selection())) return;
    this.selectionChange.emit(next);
  }

  private enterMultiMode(clickedValue: string): void {
    const currentValue = this.radioControl.value;
    this.multiViewMode.set(true);
    for (const [v, ctrl] of this.checkboxControls) {
      ctrl.setValue(v === currentValue || v === clickedValue, { emitEvent: false });
    }
    this.emitSelection({ values: this.selectedCheckboxValues(), multiViewMode: true });
  }

  private subscribeCheckboxes(): void {
    const controls = [...this.checkboxControls.values()];
    if (!controls.length) return;
    merge(...controls.map((c) => c.valueChanges))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.recalcMultiMode();
        this.emitSelection({
          values: this.currentSelection(),
          multiViewMode: this.multiViewMode(),
        });
      });
  }

  private currentSelection(): string[] {
    if (this.multiViewMode()) return this.selectedCheckboxValues();
    return [this.radioControl.value];
  }

  private selectedCheckboxValues(): string[] {
    return [...this.checkboxControls.entries()]
      .filter(([, ctrl]) => ctrl.value)
      .map(([value]) => value);
  }

  private recalcMultiMode(): void {
    const selectedCount = [...this.checkboxControls.values()].filter((c) => c.value).length;

    if (selectedCount > 1 && !this.multiViewMode()) {
      this.multiViewMode.set(true);
      return;
    }
    if (!this.multiViewMode()) return;

    if (selectedCount <= 1 && !this.shiftHeld()) {
      this.multiViewMode.set(false);
      const activeValue = this.resolveActiveValue();
      this.radioControl.setValue(activeValue, { emitEvent: false });
    }
  }

  private resolveActiveValue(): string {
    return [...this.checkboxControls.entries()].find(([, c]) => c.value)?.[0]
      ?? (this.multiSelectOptions()[0]?.value ?? '');
  }
}
